import React, { useEffect, useState, useRef, useMemo } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Swiper from 'swiper/bundle';
import 'swiper/css/bundle';
import { Link, useParams, useLocation, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/apiConfig';

const SESSION_CART_KEY = 'ccc_guest_cart';
const SESSION_WISHLIST_KEY = 'ccc_guest_wishlist';
const SESSION_SIZE_KEY = 'ccc_guest_size';
const SESSION_RETURN_KEY = 'ccc_return_to';

const safeParse = (raw, fallback) => {
  try {
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
};

const getGuestCart = () => safeParse(sessionStorage.getItem(SESSION_CART_KEY), []);
const setGuestCart = (items) => {
  try {
    sessionStorage.setItem(SESSION_CART_KEY, JSON.stringify(items || []));
  } catch {}
  window.dispatchEvent(new Event('ccc_cart_updated'));
};

const getGuestWishlist = () => safeParse(sessionStorage.getItem(SESSION_WISHLIST_KEY), []);
const setGuestWishlist = (items) => {
  try {
    sessionStorage.setItem(SESSION_WISHLIST_KEY, JSON.stringify(items || []));
  } catch {}
  window.dispatchEvent(new Event('ccc_wishlist_updated'));
};

const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

const saveGuestSize = (obj) => {
  try {
    sessionStorage.setItem(SESSION_SIZE_KEY, JSON.stringify(obj));
  } catch {}
};

const getGuestSize = () => safeParse(sessionStorage.getItem(SESSION_SIZE_KEY), null);

const clearGuestSize = () => {
  try {
    sessionStorage.removeItem(SESSION_SIZE_KEY);
  } catch {}
};

const addGuestCartItem = (item) => {
  const list = getGuestCart();
  const next = [{ ...item, guestCartId: item.guestCartId || makeId() }, ...(list || [])];
  setGuestCart(next);
};

const addGuestWishlistItem = (item) => {
  const list = getGuestWishlist();

  // prevent exact duplicates (same product+fabric+sizeType+sizeId)
  const exists = (list || []).some(
    (x) =>
      String(x.productId) === String(item.productId) &&
      String(x.fabricId) === String(item.fabricId) &&
      String(x.sizeType) === String(item.sizeType) &&
      String(x.sizeId) === String(item.sizeId)
  );

  if (exists) return;

  const next = [{ ...item, guestWishlistId: item.guestWishlistId || makeId() }, ...(list || [])];
  setGuestWishlist(next);
};

const ProductDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);

  // FIXED Parent Categories in DB -> size category key used in frontend configs
const getSizeCategoryKeyFromCategoryTitle = (titleRaw) => {
  const t = String(titleRaw || "").trim().toLowerCase();

  if (t === "shirts") return "shirt";
  if (t === "suits") return "suit";            // ✅ suits need jacket + pants together
  if (t === "sports coats") return "jacket";
  if (t === "dress pants") return "pants";

  // other parents in your DB – treat as shirt by default
  if (t === "outwear") return "shirt";
  if (t === "accessories") return "shirt";

  return "shirt";
};



  const normCat = (s) =>
  String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[\s_-]/g, "");

const categoryKey = useMemo(() => {
  const c = product?.category;

  // ✅ IMPORTANT: use PARENT category title (from API: category.parentCategory)
  const parentTitle =
    c?.parentCategory?.categoryTitle ||
    c?.parentCategory?.CategoryTitle ||
    "";

  // If parent exists, it decides the size group
  if (parentTitle) {
    return getSizeCategoryKeyFromCategoryTitle(parentTitle);
  }

  // Fallback 1: if parentCategory missing, fall back to current category title
  const selfTitle =
    c?.categoryTitle ||
    c?.CategoryTitle ||
    c?.title ||
    "";

  if (selfTitle) {
    return getSizeCategoryKeyFromCategoryTitle(selfTitle);
  }

  // Fallback 2: slug based
  const raw = c?.slug || c?.name || c?.categoryName || "";
  const x = normCat(raw);

  if (x.includes("suit")) return "suit";
if (x.includes("jacket") || x.includes("blazer") || x.includes("sportscoat")) return "jacket";
if (x.includes("dresspants")) return "pants";
if (x.includes("pant") || x.includes("trouser") || x.includes("chino")) return "pants";
return "shirt";
}, [product]);

const isAccessoriesMainCategory = useMemo(() => {
  const c = product?.category || {};
  const topTitle =
    c?.topCategory?.categoryTitle ||
    c?.topCategory?.CategoryTitle ||
    c?.parentCategory?.categoryTitle ||
    c?.parentCategory?.CategoryTitle ||
    c?.categoryTitle ||
    c?.CategoryTitle ||
    "";
  return String(topTitle).trim().toLowerCase() === "accessories";
}, [product]);




  const [selectedFabricIndex, setSelectedFabricIndex] = useState(0);
  const [mainImage, setMainImage] = useState('');
  const [zoomOpen, setZoomOpen] = useState(false);
  const [hoveredColor, setHoveredColor] = useState(null);
  const zoomedImageRef = useRef(null);
  const swiperContainerRef = useRef(null);

  const [testimonials, setTestimonials] = useState([]);
  const [hoveredPrice, setHoveredPrice] = useState(null);
  const [hoveredFabricColor, setHoveredFabricColor] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, text: '' });

  const [selectedStyleOptionIds, setSelectedStyleOptionIds] = useState([]);
  const [selectedSmartSizeId, setSelectedSmartSizeId] = useState(null);

  const storedUser = safeParse(localStorage.getItem('user'), null);
  const userId = storedUser?.userId;
  const numericUserId = userId ? parseInt(userId, 10) : null;
  const token = localStorage.getItem('token');
  const isLoggedIn = !!numericUserId && !!token;

  const [showSizePanel, setShowSizePanel] = useState(false);
  const [activeSizeTab, setActiveSizeTab] = useState('standard');
  const [smartSizes, setSmartSizes] = useState([]);
  const [bodyMeasurements, setBodyMeasurements] = useState([]);
  const [selectedBodyMeasurement, setSelectedBodyMeasurement] = useState(null);
  const [showBodyPopup, setShowBodyPopup] = useState(false);
  const [bodyForm, setBodyForm] = useState({
    neckAround: '',
    sleeveLength: '',
    shoulderWidth: '',
    chestAround: '',
    waistAround: '',
    shirtLength: '',
    bicepAround: '',
    wristAround: '',
    fit: 'Regular',
  });
  const [fitDropdownOpen, setFitDropdownOpen] = useState(false);

  const [shirtMeasurements, setShirtMeasurements] = useState([]);
  const [selectedShirtMeasurement, setSelectedShirtMeasurement] = useState(null);
  const [showShirtPopup, setShowShirtPopup] = useState(false);
  const [shirtForm, setShirtForm] = useState({
    collarAround: '',
    sleeveLength: '',
    yokeWidth: '',
    chestWidth: '',
    midsectionWidth: '',
    shirtLength: '',
    sleeveWidth: '',
    cuffAround: '',
  });

  // unified size selection for cart/wishlist
  const [selectedSizeType, setSelectedSizeType] = useState(null); // 'standard' | 'smart' | 'body' | 'shirt' | 'mail'
  const [selectedSizeId, setSelectedSizeId] = useState(null);
  const [sizeError, setSizeError] = useState(false);
  const [accessoryOptionGroups, setAccessoryOptionGroups] = useState([]);
  const [selectedAccessoryValues, setSelectedAccessoryValues] = useState({});
  const [accessoryError, setAccessoryError] = useState('');

  const [showLoginPopup, setShowLoginPopup] = useState(false);

  const requireLogin = () => setShowLoginPopup(true);

  const ensureLoggedIn = () => {
    if (!isLoggedIn) {
      requireLogin();
      return false;
    }
    return true;
  };

  const toNullableNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const goToLogin = () => {
    navigate('/login', { state: { from: location.pathname + location.search } });
  };

  // Load guest size selection on guest sessions
  useEffect(() => {
    if (isLoggedIn) return;

    const hydrateGuestSize = () => {
      const g = getGuestSize();
      setSmartSizes([]);
      setBodyMeasurements([]);
      setShirtMeasurements([]);
      if (!g?.sizeType) return;

      setSelectedSizeType(g.sizeType);
      setSelectedSizeId(g.sizeId || g.sizeType);
      setSizeError(false);

      if (g.sizeType === "standard" && g.standard && g.categoryKey === categoryKey) {
        setStandardSize((prev) => ({ ...prev, ...g.standard }));
      }

      if (g.sizeType === 'body' && g.body) {
        const guestBodyId = g.sizeId || 'guest_body';
        setBodyForm((prev) => ({ ...prev, ...g.body }));
        setBodyMeasurements([{ bodyMeasurementId: guestBodyId, ...g.body }]);
        setSelectedSizeId(guestBodyId);
      }

      if (g.sizeType === 'shirt' && g.shirt) {
        const guestShirtId = g.sizeId || 'guest_shirt';
        setShirtForm((prev) => ({ ...prev, ...g.shirt }));
        setShirtMeasurements([{ shirtMeasurementId: guestShirtId, ...g.shirt }]);
        setSelectedSizeId(guestShirtId);
      }

      if (g.sizeType === 'smart') {
        const guestSmartId = g.sizeId || 'guest_smart';
        setSelectedSizeType('smart');
        setSelectedSizeId(guestSmartId);
        if (g.smartSizeData?.name || g.smartSizeName) {
          setSmartSizes([
            { smartSizeId: guestSmartId, name: g.smartSizeName || g.smartSizeData?.name || 'Smart Size', ...(g.smartSizeData || {}) }
          ]);
        }
      }
    };

    hydrateGuestSize();
    window.addEventListener('ccc_guest_size_updated', hydrateGuestSize);
    return () => window.removeEventListener('ccc_guest_size_updated', hydrateGuestSize);
  }, [isLoggedIn, categoryKey]);

  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const openSizeGuide = () => {
  if (categoryKey !== "shirt") return;
  setShowSizeGuide(true);
};
  const closeSizeGuide = () => setShowSizeGuide(false);

  const [selectedFit, setSelectedFit] = useState('Slim');
  const [selectedTableData, setSelectedTableData] = useState([]);

  const [deliveryEstimate, setDeliveryEstimate] = useState('');

  const sizeColumns = ["38", "40", "42", "44", "46", "48", "50"];

  const tableRowLabels = [
    "Length(Back)",
    "Chest",
    "Stomach",
    "Hips",
    "Shoulder",
    "Sleeve",
    "Biceps",
    "Cuffs",
    "Neck"
  ];

  const shirtSizeGuideData = {
    Slim: {
      "38": [28, 40, 38.5, 40, 17.5, 24.5, 14.5, 9.25, 14.75],
      "40": [29.5, 43, 40.5, 41.5, 17.75, 25.25, 15, 9.75, 16],
      "42": [30.5, 45, 42.5, 44, 18.25, 25.5, 15.5, 10.5, 16],
      "44": [30.5, 45, 44, 45, 18.5, 25.5, 15.5, 10.25, 16.75],
      "46": [31.5, 48.5, 46.5, 47.5, 19.25, 25.5, 17.5, 10.25, 16.75],
      "48": [33.5, 51, 49.5, 50.5, 19.5, 26.25, 16.5, 10.5, 17.25],
      "50": [34, 54, 50.5, 52, 20, 26, 18, 11, 17.75]
    },
    Athletic: {
      "38": [27.75, 42.5, 40.5, 42, 17.5, 24.5, 16, 9.5, 14.75],
      "40": [29, 44, 42.5, 43.5, 18, 24.75, 16.5, 10.75, 15.5],
      "42": [30, 45, 44.5, 46, 18.25, 25.14, 16.5, 10, 16],
      "44": [30, 48, 46.5, 47.5, 18.5, 25.75, 17.5, 10.25, 16.25],
      "46": [31.5, 50, 48.5, 49, 18.5, 25.25, 18, 10.5, 16.75],
      "48": [33, 52, 50.5, 50.5, 19.5, 26, 18.5, 10.5, 17.75],
      "50": [34, 54.5, 52, 54, 20, 26, 18.5, 11, 17.75]
    },
    Classic: {
      "38": [28, 41.5, 39, 41, 17.5, 24.75, 15, 9.5, 14.75],
      "40": [29, 42.5, 41, 42, 18, 24.5, 16, 9.75, 15.75],
      "42": [29.5, 44.5, 43, 44, 18.25, 25.25, 16, 10, 15.75],
      "44": [30.5, 47, 46, 46.5, 18.5, 25, 16.5, 10.5, 16.5],
      "46": [31.5, 50, 47, 48.5, 18.75, 25.5, 18, 10.5, 17],
      "48": [33, 50.5, 49, 50, 19, 26, 17.5, 10.5, 17.25],
      "50": [34, 53, 51.5, 52, 20, 26.25, 18.5, 11, 17.75]
    }
  };

  const [alsoSuggestedProducts, setAlsoSuggestedProducts] = useState([]);

  // Measurement videos
  const [measurementVideos, setMeasurementVideos] = useState({ Body: {}, Shirt: {} });
  const [activeBodyField, setActiveBodyField] = useState('neckAround');
  const [activeShirtField, setActiveShirtField] = useState('collarAround');

 const norm = (s) =>
  String(s || '')
    .toLowerCase()
    .trim()
    .replace(/[\s_-]/g, ''); // removes spaces, underscores, hyphens

const normalizeSection = (s) => {
  const x = norm(s);
  // API might return: "Measure Your Body", "Body Measurement", "Body", etc.
  if (x.includes('body')) return 'body';
  if (x.includes('shirt')) return 'shirt';
  return x;
};

const FIELD_ALIASES = {
  // Body fields (API might store shorter keys)
  neck: 'neckaround',
  neckaround: 'neckaround',

  sleeve: 'sleevelength',
  sleevelength: 'sleevelength',

  shoulder: 'shoulderwidth',
  shoulderwidth: 'shoulderwidth',

  chest: 'chestaround',
  chestaround: 'chestaround',

  waist: 'waistaround',
  waistaround: 'waistaround',

  bicep: 'biceparound',
  biceparound: 'biceparound',

  wrist: 'wristaround',
  wristaround: 'wristaround',

  shirtlength: 'shirtlength',

  // Shirt fields
  collar: 'collararound',
  collararound: 'collararound',

  yoke: 'yokewidth',
  yokewidth: 'yokewidth',

  chestwidth: 'chestwidth',
  midsection: 'midsectionwidth',
  midsectionwidth: 'midsectionwidth',

  sleevewidth: 'sleevewidth',
  cuff: 'cuffaround',
  cuffaround: 'cuffaround',
};

const normalizeFieldKey = (k) => {
  const x = norm(k);
  return FIELD_ALIASES[x] || x;
};

const getMeasurementVideoUrl = (section, fieldKey) => {
  const sec = normalizeSection(section); // 'body' | 'shirt'
  const wantKey = normalizeFieldKey(fieldKey); // normalized key like 'neckaround'

  const bodyMap = measurementVideos?.Body || {};
  const shirtMap = measurementVideos?.Shirt || {};

  const map = sec === 'body' ? bodyMap : sec === 'shirt' ? shirtMap : {};
  
  // map keys in API are camelCase already; normalize both sides
  const foundKey = Object.keys(map).find((k) => normalizeFieldKey(k) === wantKey);
  return foundKey ? map[foundKey] : '';
};

const getMeasurementVideo = (section, fieldKey) => {
  const sec = normalizeSection(section); // 'body' | 'shirt'
  const key = normalizeFieldKey(fieldKey);

  const bodyMap = measurementVideos?.Body || {};
  const shirtMap = measurementVideos?.Shirt || {};

  if (sec === 'body') return bodyMap[key] || null;
  if (sec === 'shirt') return shirtMap[key] || null;
  return null;
};

const chunkBy = (arr, size) => {
  const out = [];
  for (let i = 0; i < (arr || []).length; i += size) out.push(arr.slice(i, i + size));
  return out;
};


  const toEmbedUrl = (video) => {
  if (!video) return '';

  // -------------------------------------------------
  // CASE 1: API already gives a string URL (most important)
  // -------------------------------------------------
  if (typeof video === 'string') {
    const raw = video.trim();
    if (!raw) return '';

    // already embed
    if (raw.includes('/embed/')) return raw;

    try {
      const url = new URL(raw);

      // youtu.be/VIDEO_ID
      if (url.hostname.includes('youtu.be')) {
        const id = url.pathname.replace('/', '').trim();
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }

      // youtube.com/watch?v=VIDEO_ID
      const v = url.searchParams.get('v');
      if (v) return `https://www.youtube.com/embed/${v}`;

      // youtube.com/shorts/VIDEO_ID
      if (url.pathname.includes('/shorts/')) {
        const id = url.pathname.split('/shorts/')[1]?.split(/[?/]/)[0];
        return id ? `https://www.youtube.com/embed/${id}` : '';
      }

      // fallback last path segment
      const parts = url.pathname.split('/').filter(Boolean);
      const maybeId = parts[parts.length - 1];
      return maybeId ? `https://www.youtube.com/embed/${maybeId}` : '';
    } catch {
      // raw might already be a youtube id
      const cleaned = raw.replace(/\s/g, '');
      if (cleaned.length >= 6 && !cleaned.includes('http')) {
        return `https://www.youtube.com/embed/${cleaned}`;
      }
      return '';
    }
  }

  // -------------------------------------------------
  // CASE 2: API returns an object
  // -------------------------------------------------

  // If backend already provides embedUrl
  if (video.embedUrl && String(video.embedUrl).trim()) {
    return String(video.embedUrl).trim();
  }

  // Resolve raw source
  const raw =
    (video.videoUrl && String(video.videoUrl).trim()) ||
    (video.youtubeId && `https://www.youtube.com/watch?v=${String(video.youtubeId).trim()}`) ||
    '';

  if (!raw) return '';

  // already embed
  if (raw.includes('/embed/')) return raw;

  try {
    const url = new URL(raw);

    // youtu.be/VIDEO_ID
    if (url.hostname.includes('youtu.be')) {
      const id = url.pathname.replace('/', '').trim();
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }

    // youtube.com/watch?v=VIDEO_ID
    const v = url.searchParams.get('v');
    if (v) return `https://www.youtube.com/embed/${v}`;

    // youtube.com/shorts/VIDEO_ID
    if (url.pathname.includes('/shorts/')) {
      const id = url.pathname.split('/shorts/')[1]?.split(/[?/]/)[0];
      return id ? `https://www.youtube.com/embed/${id}` : '';
    }

    // fallback last path segment
    const parts = url.pathname.split('/').filter(Boolean);
    const maybeId = parts[parts.length - 1];
    return maybeId ? `https://www.youtube.com/embed/${maybeId}` : '';
  } catch {
    // raw might be just an id
    const cleaned = raw.replace(/\s/g, '');
    if (cleaned.length >= 6 && !cleaned.includes('http')) {
      return `https://www.youtube.com/embed/${cleaned}`;
    }
    return '';
  }
};



  useEffect(() => {
  const tkn = localStorage.getItem('token');

  const buildMapsFromArray = (arr) => {
    const Body = {};
    const Shirt = {};

    (arr || []).forEach((row) => {
      const section = normalizeSection(row.section || row.type || row.category || '');
      const fieldKey =
        row.fieldKey ||
        row.field ||
        row.measurementField ||
        row.inputKey ||
        row.key ||
        '';

      if (!fieldKey) return;

      const normalizedField = normalizeFieldKey(fieldKey);

      // store the whole row as the "video object"
      if (section === 'body') Body[normalizedField] = row;
      if (section === 'shirt') Shirt[normalizedField] = row;
    });

    return { Body, Shirt };
  };

  const normalizeObjectMaps = (obj) => {
    // accept Body/body, Shirt/shirt (any casing)
    const bodyRaw = obj?.Body || obj?.body || obj?.BODY || {};
    const shirtRaw = obj?.Shirt || obj?.shirt || obj?.SHIRT || {};

    const Body = {};
    const Shirt = {};

    // normalize keys inside each map
    Object.keys(bodyRaw || {}).forEach((k) => {
      Body[normalizeFieldKey(k)] = bodyRaw[k];
    });

    Object.keys(shirtRaw || {}).forEach((k) => {
      Shirt[normalizeFieldKey(k)] = shirtRaw[k];
    });

    return { Body, Shirt };
  };

  fetch(`${API_BASE_URL}/api/measurement-videos`, {
    headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      let normalized = { Body: {}, Shirt: {} };

      if (Array.isArray(data)) {
        normalized = buildMapsFromArray(data);
      } else if (data && typeof data === 'object') {
        normalized = normalizeObjectMaps(data);
      }

      setMeasurementVideos(normalized);

      // quick debug (remove later)
      console.log('measurementVideos normalized:', normalized);
    })
    .catch((err) => console.error('Error fetching measurement videos:', err));
}, []);


  const activeBodyVideo = getMeasurementVideo('body', activeBodyField);
  const activeShirtVideo = getMeasurementVideo('shirt', activeShirtField);

  const activeBodyVideoUrl = toEmbedUrl(activeBodyVideo);
  const activeShirtVideoUrl = toEmbedUrl(activeShirtVideo);


  // ---------- STANDARD SIZES ----------
  const [standardSize, setStandardSize] = useState(() => {
  if (categoryKey === "suit") {
    return {
      jacketSize: "", jacketLength: "", jacketFit: "",
      waistSize: "", inseamLength: "", pantFit: ""
    };
  }
  if (categoryKey === "jacket") return { jacketSize: "", jacketLength: "", jacketFit: "" };
  if (categoryKey === "pants") return { waistSize: "", inseamLength: "", pantFit: "" };
  return { collarSize: "", sleeveLength: "", fit: "" };
});


  const [standardSizeList, setStandardSizeList] = useState([]);

  const COLLAR_OPTIONS = [
    { value: '14.5', label: 'XS' },
    { value: '15', label: 'S' },
    { value: '15.5', label: 'M' },
    { value: '16', label: 'M/L' },
    { value: '16.5', label: 'L' },
    { value: '17', label: 'XL' },
    { value: '17.5', label: 'XL' },
    { value: '18', label: '2XL' },
    { value: '18.5', label: '2XL' },
    { value: '19', label: '3XL' },
    { value: '20', label: '4XL' },
    { value: '21', label: '5XL' },
  ];

  // when collar is 14.5 or 15 → use 32–35
  const SLEEVE_OPTIONS_SMALL_COLLAR = [
    { value: '32', label: `5'6"–5'8"` },
    { value: '33', label: `5'8"–5'9"` },
    { value: '34', label: `5'10"–5'11"` },
    { value: '35', label: `6'0"–6'1"` },
  ];

  // when collar is 15.5 and above → use 33–36
  const SLEEVE_OPTIONS_LARGE_COLLAR = [
    { value: '33', label: `5'8"–5'9"` },
    { value: '34', label: `5'10"–5'11"` },
    { value: '35', label: `6'0"–6'1"` },
    { value: '36', label: `6'2"–6'3"` },
  ];

  const FIT_OPTIONS = [
    { value: 'Slim', label: 'Slim' },
    { value: 'Classic', label: 'Classic' },
    { value: 'Athletic', label: 'Athletic' },
  ];

  const STANDARD_CONFIG = {
  shirt: {
    title: "Standard Sizes",
    fields: {
      collarSize: {
        label: "COLLAR SIZE",
        options: COLLAR_OPTIONS.map((o) => ({ value: String(o.value), sub: o.label })),
      },
      sleeveLength: {
        label: "SLEEVE LENGTH",
        dynamic: "shirtSleeveByCollar",
      },
      fit: {
        label: "FIT",
        options: FIT_OPTIONS.map((o) => ({ value: o.value, sub: o.value === "Slim" ? "Most Popular" : "" })),
      },
    },
    tabs: ["standard", "smart", "body", "shirt", "mail"],
  },

  jacket: {
    title: "Standard Jacket Sizes",
    fields: {
      jacketSize: {
        label: "JACKET SIZE",
        options: [
          { value: "36", sub: "XS" },
          { value: "38", sub: "S" },
          { value: "40", sub: "M" },
          { value: "42", sub: "L" },
          { value: "44", sub: "L/XL" },
          { value: "46", sub: "XL" },
          { value: "48", sub: "2XL" },
          { value: "50", sub: "2XL" },
          { value: "52", sub: "3XL" },
        ],
      },
      jacketLength: {
        label: "LENGTH",
        options: [
          { value: "short", sub: `5'6"–5'8"` },
          { value: "regular", sub: `5'8"–6'0"` },
          { value: "long", sub: `6'0"–6'3"` },
        ],
      },
      jacketFit: {
        label: "FIT",
        options: [
          { value: "Slim", sub: "Most Popular" },
          { value: "Classic", sub: "" },
          { value: "Athletic", sub: "" },
        ],
      },
    },
    tabs: ["standard", "smart"],
  },

  suit: {
  title: "Standard Suit Sizes",
  fields: {
    // Jacket fields
    jacketSize: {
      label: "JACKET SIZE",
      options: [
        { value: "36", sub: "XS" },
        { value: "38", sub: "S" },
        { value: "40", sub: "M" },
        { value: "42", sub: "L" },
        { value: "44", sub: "L/XL" },
        { value: "46", sub: "XL" },
        { value: "48", sub: "2XL" },
        { value: "50", sub: "2XL" },
        { value: "52", sub: "3XL" },
      ],
    },
    jacketLength: {
      label: "JACKET LENGTH",
      options: [
        { value: "short", sub: `5'6"–5'8"` },
        { value: "regular", sub: `5'8"–6'0"` },
        { value: "long", sub: `6'0"–6'3"` },
      ],
    },
    jacketFit: {
      label: "JACKET FIT",
      options: [
        { value: "Slim", sub: "Most Popular" },
        { value: "Classic", sub: "" },
        { value: "Athletic", sub: "" },
      ],
    },

    // Pants fields
    waistSize: {
      label: "WAIST",
      options: ["28","30","32","34","36","38","40","42"].map((v) => ({ value: v, sub: "" })),
    },
    inseamLength: {
      label: "INSEAM",
      options: ["28","30","32","34","36"].map((v) => ({ value: v, sub: "" })),
    },
    pantFit: {
      label: "PANT FIT",
      options: [
        { value: "Slim", sub: "Most Popular" },
        { value: "Regular", sub: "" },
        { value: "Relaxed", sub: "" },
      ],
    },
  },
  tabs: ["standard", "smart"],
},


  pants: {
    title: "Standard Pants Sizes",
    fields: {
      waistSize: {
        label: "WAIST",
        options: ["28","30","32","34","36","38","40","42"].map((v) => ({ value: v, sub: "" })),
      },
      inseamLength: {
        label: "INSEAM",
        options: ["28","30","32","34","36"].map((v) => ({ value: v, sub: "" })),
      },
      pantFit: {
        label: "FIT",
        options: [
          { value: "Slim", sub: "Most Popular" },
          { value: "Regular", sub: "" },
          { value: "Relaxed", sub: "" },
        ],
      },
    },
    tabs: ["standard", "smart"],
  },
};



const standardCfg = useMemo(() => {
  return STANDARD_CONFIG[categoryKey] || STANDARD_CONFIG.shirt;
}, [categoryKey]);

const fabricRows = useMemo(() => {
  return chunkBy(product?.fabrics || [], 7); // ✅ break after 10 swatches
}, [product?.fabrics]);




  const [standardSizeSaved, setStandardSizeSaved] = useState(null);

  const sleeveOptionsForCurrentCollar = useMemo(() => {
  if (categoryKey !== "shirt") return [];
  const collar = parseFloat(standardSize.collarSize);
  if (!collar) return SLEEVE_OPTIONS_LARGE_COLLAR;
  if (collar <= 15) return SLEEVE_OPTIONS_SMALL_COLLAR;
  return SLEEVE_OPTIONS_LARGE_COLLAR;
}, [categoryKey, standardSize.collarSize]);


  useEffect(() => {
  if (categoryKey !== "shirt") return;
  if (!standardSize.sleeveLength) return;

  const stillValid = sleeveOptionsForCurrentCollar.some(
    (opt) => opt.value === standardSize.sleeveLength
  );

  if (!stillValid) {
    setStandardSize((prev) => ({ ...prev, sleeveLength: "" }));
  }
}, [categoryKey, sleeveOptionsForCurrentCollar, standardSize.sleeveLength]);


  useEffect(() => {
  if (categoryKey === "suit") {
    setStandardSize({
      jacketSize: "", jacketLength: "", jacketFit: "",
      waistSize: "", inseamLength: "", pantFit: ""
    });
  } else if (categoryKey === "jacket") {
    setStandardSize({ jacketSize: "", jacketLength: "", jacketFit: "" });
  } else if (categoryKey === "pants") {
    setStandardSize({ waistSize: "", inseamLength: "", pantFit: "" });
  } else {
    setStandardSize({ collarSize: "", sleeveLength: "", fit: "" });
  }
}, [categoryKey]);



useEffect(() => {
  if (!standardCfg.tabs.includes(activeSizeTab)) {
    setActiveSizeTab(standardCfg.tabs[0] || "standard");
  }
}, [categoryKey]); // intentionally only on category change


  useEffect(() => {
  if (!isLoggedIn) return;

  const normalize = (v) => (v === null || v === undefined ? "" : String(v).trim());

  // backend row may store: "Shirts / Suits / Pants" etc.
  const normalizeRecordCategory = (raw) => {
    const t = String(raw || "").toLowerCase();
    if (t.includes("shirts")) return "shirt";
    if (t.includes("dress pants")) return "pants";
    if (t.includes("trousers")) return "pants";
    if (t.includes("suits")) return "jacket";
    if (t.includes("sports coats")) return "jacket";
    if (t.includes("coat")) return "jacket";
    if (t.includes("outwear")) return "jacket";
    return "shirt";
  };

  const abort = new AbortController();

  fetch(`${API_BASE_URL}/UserManagement/GetStandardSizes/${numericUserId}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal: abort.signal,
  })
    .then((res) => (res.ok ? res.json() : null))
    .then((data) => {
      const all = Array.isArray(data) ? data : data ? [data] : [];

      const filtered = all.filter((r) => {
        const rk = (r.categoryKey ?? r.CategoryKey ?? r.category ?? r.Category ?? "");
        return String(rk).toLowerCase().trim() === String(categoryKey).toLowerCase().trim();
      });


      setStandardSizeList(filtered);

      const record = filtered[0] || null;
      setStandardSizeSaved(record);

      // fill/reset based on CURRENT category fields
      const fieldKeys = Object.keys(standardCfg.fields);

      if (record) {
        const next = {};
        fieldKeys.forEach((k) => {
          next[k] = normalize(record[k]);
        });
        setStandardSize((prev) => ({ ...prev, ...next }));
      } else {
        const blank = {};
        fieldKeys.forEach((k) => (blank[k] = ""));
        setStandardSize(blank);
      }
    })
    .catch((err) => {
      if (err?.name === "AbortError") return;

      console.error("Error loading standard sizes:", err);
      setStandardSizeList([]);
      setStandardSizeSaved(null);

      const blank = {};
      Object.keys(standardCfg.fields).forEach((k) => (blank[k] = ""));
      setStandardSize(blank);
    });

  return () => abort.abort();
}, [numericUserId, token, isLoggedIn, categoryKey, standardCfg]);




  const handleStandardSelect = (key, value) => {
    setStandardSize((prev) => ({ ...prev, [key]: value }));
  };

  const handleEditStandardSize = (record) => {
  if (!record) return;

  const normalize = (v) => (v === null || v === undefined ? "" : String(v).trim());

  const next = {};
  Object.keys(standardCfg.fields).forEach((k) => {
    next[k] = normalize(record[k]);
  });

  setStandardSize((prev) => ({ ...prev, ...next }));
  setStandardSizeSaved(record);
};


  const handleSaveStandardSize = async () => {
    const requiredKeys = Object.keys(standardCfg.fields);
const missing = requiredKeys.filter((k) => !standardSize[k]);
if (missing.length) {
  alert(`Please select: ${missing.map((k) => standardCfg.fields[k].label).join(", ")}`);
  return;
}


    // ---------- GUEST FLOW ----------
    // ---------- GUEST FLOW ----------
if (!isLoggedIn) {
  saveGuestSize({
  sizeType: "standard",
  sizeId: `guest_standard_${categoryKey}`,
  categoryKey,
  standard: { ...standardSize },
});

  setSelectedSizeType("standard");
  setSelectedSizeId(`guest_standard_${categoryKey}`);
  setSizeError(false);
  setShowSizePanel(false);
  return;
}


    // ---------- LOGGED-IN FLOW ----------
const standardSizeSelectionId = Number(standardSizeSaved?.standardSizeSelectionId);
const isStandardUpdate = Number.isInteger(standardSizeSelectionId) && standardSizeSelectionId > 0;
const payload = {
  userId: numericUserId,
  standardSizeSelectionId: isStandardUpdate ? standardSizeSelectionId : 0,

  // IMPORTANT: send category so backend stores correctly
  categoryKey: categoryKey, // if backend expects "Shirts / Pants" etc, map it there instead

  // send only fields relevant for this category
  ...Object.keys(standardCfg.fields).reduce((acc, k) => {
    if (['collarSize', 'sleeveLength', 'jacketSize', 'waistSize', 'inseamLength'].includes(k)) {
      acc[k] = toNullableNumber(standardSize[k]);
    } else {
      acc[k] = standardSize[k] || null;
    }
    return acc;
  }, {}),
};

const url = isStandardUpdate
  ? `${API_BASE_URL}/UserManagement/UpdateStandardSize`
  : `${API_BASE_URL}/UserManagement/AddStandardSize`;


    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      alert('Failed to save standard size');
      return;
    }

    const saved = await res.json();

    setStandardSizeList((prev) => {
      if (!prev || prev.length === 0) return [saved];
      const exists = prev.some((s) => s.standardSizeSelectionId === saved.standardSizeSelectionId);
      return exists
        ? prev.map((s) =>
            s.standardSizeSelectionId === saved.standardSizeSelectionId ? saved : s
          )
        : [...prev, saved];
    });

    setSelectedSizeType('standard');
    setSelectedSizeId(saved.standardSizeSelectionId);
    setSizeError(false);
    setShowSizePanel(false);

    setStandardSizeSaved(null);

if (categoryKey === "suit") {
  setStandardSize({
    jacketSize: "", jacketLength: "", jacketFit: "",
    waistSize: "", inseamLength: "", pantFit: ""
  });
} else if (categoryKey === "jacket") {
  setStandardSize({ jacketSize: "", jacketLength: "", jacketFit: "" });
} else if (categoryKey === "pants") {
  setStandardSize({ waistSize: "", inseamLength: "", pantFit: "" });
} else {
  setStandardSize({ collarSize: "", sleeveLength: "", fit: "" });
}

  };

  // ----------------------------------------------------------

  useEffect(() => {
    handleUpdateSize('Slim');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpdateSize = (fit) => {
    setSelectedFit(fit);
    const fitKey = fit;
    const fitRows = shirtSizeGuideData[fitKey] || shirtSizeGuideData.Slim;

    const rows = tableRowLabels.map((_, rowIndex) =>
      sizeColumns.map((size) => (fitRows[size] ? fitRows[size][rowIndex] : '-'))
    );
    setSelectedTableData(rows);
  };

  // Shirt measurements
  useEffect(() => {
    if (!isLoggedIn) return;

    fetch(`${API_BASE_URL}/UserManagement/GetShirtMeasurements/${numericUserId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.json())
      .then((data) => setShirtMeasurements(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Error loading shirt measurements:', err));
  }, [numericUserId, token, isLoggedIn]);

  const handleShirtInputChange = (key, value) => {
    setShirtForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveShirtMeasurement = async () => {
    // ---------- GUEST FLOW ----------
    if (!isLoggedIn) {
      const guestShirtId = selectedShirtMeasurement?.shirtMeasurementId || `guest_shirt_${Date.now()}`;
      setShirtMeasurements([{ shirtMeasurementId: guestShirtId, ...shirtForm }]);
      saveGuestSize({
        sizeType: 'shirt',
        sizeId: guestShirtId,
        shirt: { ...shirtForm },
      });

      setSelectedSizeType('shirt');
      setSelectedSizeId(guestShirtId);
      setSizeError(false);

      setShowShirtPopup(false);
      setShowSizePanel(false);
      return;
    }

    // ---------- LOGGED-IN FLOW ----------
    const shirtMeasurementId = Number(selectedShirtMeasurement?.shirtMeasurementId);
    const isShirtUpdate = Number.isInteger(shirtMeasurementId) && shirtMeasurementId > 0;
    const payload = {
      userId: numericUserId,
      shirtMeasurementId: isShirtUpdate ? shirtMeasurementId : 0,
      collarAround: toNullableNumber(shirtForm.collarAround),
      sleeveLength: toNullableNumber(shirtForm.sleeveLength),
      yokeWidth: toNullableNumber(shirtForm.yokeWidth),
      chestWidth: toNullableNumber(shirtForm.chestWidth),
      midsectionWidth: toNullableNumber(shirtForm.midsectionWidth),
      shirtLength: toNullableNumber(shirtForm.shirtLength),
      sleeveWidth: toNullableNumber(shirtForm.sleeveWidth),
      cuffAround: toNullableNumber(shirtForm.cuffAround),
    };

    const url = isShirtUpdate
      ? `${API_BASE_URL}/UserManagement/UpdateShirtMeasurement`
      : `${API_BASE_URL}/UserManagement/AddShirtMeasurement`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      alert('Failed to save measurement');
      return;
    }

    const saved = await res.json();

    setSelectedSizeType('shirt');
    setSelectedSizeId(saved.shirtMeasurementId);
    setSizeError(false);

    setShirtMeasurements((prev) => {
      const exists = prev.some((m) => m.shirtMeasurementId === saved.shirtMeasurementId);
      return exists
        ? prev.map((m) => (m.shirtMeasurementId === saved.shirtMeasurementId ? saved : m))
        : [...prev, saved];
    });

    setShowShirtPopup(false);
    setSelectedShirtMeasurement(null);
    setShirtForm({
      collarAround: '',
      sleeveLength: '',
      yokeWidth: '',
      chestWidth: '',
      midsectionWidth: '',
      shirtLength: '',
      sleeveWidth: '',
      cuffAround: '',
    });
  };

  const handleDeleteShirtMeasurement = async (delId) => {
    if (!ensureLoggedIn()) return;
    if (!window.confirm('Are you sure you want to delete this measurement?')) return;

    await fetch(`${API_BASE_URL}/UserManagement/DeleteShirtMeasurement/${delId}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    setShirtMeasurements((prev) => prev.filter((m) => m.shirtMeasurementId !== delId));

    if (selectedSizeType === 'shirt' && selectedSizeId === delId) {
      setSelectedSizeType(null);
      setSelectedSizeId(null);
    }
  };

  // Body measurements
  useEffect(() => {
    if (!isLoggedIn) return;

    fetch(`${API_BASE_URL}/UserManagement/GetBodyMeasurements/${numericUserId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.json())
      .then((data) => setBodyMeasurements(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Error loading body measurements:', err));
  }, [numericUserId, token, isLoggedIn]);

  const handleBodyInputChange = (key, value) => {
    setBodyForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleFitSelect = (value) => {
    setBodyForm((prev) => ({ ...prev, fit: value }));
    setFitDropdownOpen(false);
  };

  const handleSaveBodyMeasurement = async () => {
    // ---------- GUEST FLOW ----------
    if (!isLoggedIn) {
      const guestBodyId = selectedBodyMeasurement?.bodyMeasurementId || `guest_body_${Date.now()}`;
      setBodyMeasurements([{ bodyMeasurementId: guestBodyId, ...bodyForm }]);
      saveGuestSize({
        sizeType: 'body',
        sizeId: guestBodyId,
        body: { ...bodyForm },
      });

      setSelectedSizeType('body');
      setSelectedSizeId(guestBodyId);
      setSizeError(false);

      setShowBodyPopup(false);
      setShowSizePanel(false);
      return;
    }

    // ---------- LOGGED-IN FLOW ----------
    const bodyMeasurementId = Number(selectedBodyMeasurement?.bodyMeasurementId);
    const isBodyUpdate = Number.isInteger(bodyMeasurementId) && bodyMeasurementId > 0;
    const payload = {
      userId: numericUserId,
      bodyMeasurementId: isBodyUpdate ? bodyMeasurementId : 0,
      neckAround: toNullableNumber(bodyForm.neckAround),
      sleeveLength: toNullableNumber(bodyForm.sleeveLength),
      shoulderWidth: toNullableNumber(bodyForm.shoulderWidth),
      chestAround: toNullableNumber(bodyForm.chestAround),
      waistAround: toNullableNumber(bodyForm.waistAround),
      shirtLength: toNullableNumber(bodyForm.shirtLength),
      bicepAround: toNullableNumber(bodyForm.bicepAround),
      wristAround: toNullableNumber(bodyForm.wristAround),
      fit: bodyForm.fit || null,
    };

    const url = isBodyUpdate
      ? `${API_BASE_URL}/UserManagement/UpdateBodyMeasurement`
      : `${API_BASE_URL}/UserManagement/AddBodyMeasurement`;

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      alert('Failed to save measurement');
      return;
    }

    const saved = await res.json();

    setSelectedSizeType('body');
    setSelectedSizeId(saved.bodyMeasurementId);
    setSizeError(false);

    setBodyMeasurements((prev) => {
      if (selectedBodyMeasurement) {
        return prev.map((m) => (m.bodyMeasurementId === saved.bodyMeasurementId ? saved : m));
      }
      return [...prev, saved];
    });

    setShowBodyPopup(false);
    setSelectedBodyMeasurement(null);
    setBodyForm({
      neckAround: '',
      sleeveLength: '',
      shoulderWidth: '',
      chestAround: '',
      waistAround: '',
      shirtLength: '',
      bicepAround: '',
      wristAround: '',
      fit: 'Regular',
    });
  };

  const handleDeleteBodyMeasurement = async (delId) => {
    if (!ensureLoggedIn()) return;
    if (!window.confirm('Are you sure you want to delete this measurement?')) return;

    await fetch(`${API_BASE_URL}/UserManagement/DeleteBodyMeasurement/${delId}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    setBodyMeasurements((prev) => prev.filter((m) => m.bodyMeasurementId !== delId));

    if (selectedSizeType === 'body' && selectedSizeId === delId) {
      setSelectedSizeType(null);
      setSelectedSizeId(null);
    }
  };

  useEffect(() => {
    if (selectedBodyMeasurement) {
      setBodyForm({
        neckAround: selectedBodyMeasurement.neckAround || '',
        sleeveLength: selectedBodyMeasurement.sleeveLength || '',
        shoulderWidth: selectedBodyMeasurement.shoulderWidth || '',
        chestAround: selectedBodyMeasurement.chestAround || '',
        waistAround: selectedBodyMeasurement.waistAround || '',
        shirtLength: selectedBodyMeasurement.shirtLength || '',
        bicepAround: selectedBodyMeasurement.bicepAround || '',
        wristAround: selectedBodyMeasurement.wristAround || '',
        fit: selectedBodyMeasurement.fit || 'Regular',
      });
    } else {
      setBodyForm({
        neckAround: '',
        sleeveLength: '',
        shoulderWidth: '',
        chestAround: '',
        waistAround: '',
        shirtLength: '',
        bicepAround: '',
        wristAround: '',
        fit: 'Regular',
      });
    }
  }, [selectedBodyMeasurement]);

  // Smart sizes
  useEffect(() => {
    if (!isLoggedIn) return;

    fetch(`${API_BASE_URL}/UserManagement/GetSmartSizes/${numericUserId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => res.json())
      .then((data) => setSmartSizes(Array.isArray(data) ? data : []))
      .catch((err) => console.error('Error fetching smart sizes:', err));
  }, [numericUserId, token, isLoggedIn]);

  const handleEditSmartSize = (smartSize) => {
    localStorage.setItem('editSmartSize', JSON.stringify(smartSize));

    // guest still allowed to edit locally in wizard (it will save to ccc_guest_size)
    navigate(isLoggedIn ? `/smartSizes/edit/${numericUserId}` : `/smartSizes/edit/0`);
  };

  // testimonials
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/testimonials?page=Product`)
      .then((res) => res.json())
      .then((data) => setTestimonials(data))
      .catch((err) => console.error('Error fetching testimonials:', err));
  }, []);

  const calculateEstimatedDelivery = (minDays, maxDays) => {
    const today = new Date();

    const minDate = new Date(today);
    minDate.setDate(today.getDate() + minDays);

    const maxDate = new Date(today);
    maxDate.setDate(today.getDate() + maxDays);

    const formatDate = (date) => date.toLocaleString('en-US', { month: 'short', day: 'numeric' });

    if (minDays === maxDays) return formatDate(maxDate);
    return `${formatDate(minDate)}–${formatDate(maxDate)}`;
  };

  const getDeliveryWindow = (productPayload) => {
    const c = productPayload?.category || productPayload?.Category || null;
    if (!c) return { minDays: null, maxDays: null };

    const buckets = [
      c,
      c?.parentCategory,
      c?.ParentCategory,
      c?.topCategory,
      c?.TopCategory
    ].filter(Boolean);

    for (const b of buckets) {
      const minRaw = b?.minDeliveryDays ?? b?.MinDeliveryDays;
      const maxRaw = b?.maxDeliveryDays ?? b?.MaxDeliveryDays;
      const minDays = Number(minRaw);
      const maxDays = Number(maxRaw);
      if (Number.isFinite(minDays) && Number.isFinite(maxDays) && minDays > 0 && maxDays > 0) {
        return { minDays, maxDays };
      }
    }

    return { minDays: null, maxDays: null };
  };

  // product fetch (supports ?fabricId=)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const requestedFabricId = params.get('fabricId') ? parseInt(params.get('fabricId'), 10) : null;

    fetch(`${API_BASE_URL}/api/insideproducts/${id}`)
      .then((res) => res.json())
      .then((data) => {
        setProduct(data);
        setAlsoSuggestedProducts(data.alsoSuggestedProducts || []);

        let initialIndex = 0;
        if (requestedFabricId && Array.isArray(data.fabrics)) {
          const idx = data.fabrics.findIndex((f) => f.fabricID === requestedFabricId);
          if (idx >= 0) initialIndex = idx;
        }

        setSelectedFabricIndex(initialIndex);

        const initialFabric = data.fabrics?.[initialIndex];
        const firstImg =
          initialFabric?.images?.[0]?.imageURL ||
          initialFabric?.displayImage ||
          '/assets/images/placeholder.jpg';

        setMainImage(firstImg);

        const { minDays, maxDays } = getDeliveryWindow(data);
        if (minDays !== null && maxDays !== null) {
          setDeliveryEstimate(calculateEstimatedDelivery(minDays, maxDays));
        } else {
          setDeliveryEstimate('');
        }
      })
      .catch((err) => console.error('Error fetching product:', err));
  }, [id, location.search]);

  useEffect(() => {
    if (!isAccessoriesMainCategory) {
      setAccessoryOptionGroups([]);
      setSelectedAccessoryValues({});
      setAccessoryError('');
      return;
    }

    let parsed = null;
    try {
      parsed = product?.accessoryOptionsJson ? JSON.parse(product.accessoryOptionsJson) : null;
    } catch {
      parsed = null;
    }

    const groups = Array.isArray(parsed?.groups)
      ? parsed.groups
          .map((g) => ({
            key: String(g?.key || '').trim(),
            label: String(g?.label || g?.key || '').trim(),
            displayType: String(g?.displayType || 'dropdown').trim().toLowerCase(),
            required: !!g?.required,
            options: Array.isArray(g?.options)
              ? g.options
                  .map((o) => ({
                    label: String(o?.label || o?.value || '').trim(),
                    value: String(o?.value || o?.label || '').trim(),
                    swatchImageUrl: o?.swatchImageUrl || '',
                    priceImpact: Number(o?.priceImpact || 0) || 0,
                  }))
                  .filter((o) => o.label && o.value)
              : [],
          }))
          .filter((g) => g.key && g.label && g.options.length > 0)
      : [];

    setAccessoryOptionGroups(groups);

    const defaults = {};
    groups.forEach((g) => {
      if (g.required && g.options.length > 0) {
        defaults[g.key] = g.options[0].value;
      }
    });
    setSelectedAccessoryValues(defaults);
  }, [product, isAccessoriesMainCategory]);

  // zoom
  useEffect(() => {
    const zoomedImage = zoomedImageRef.current;
    if (!zoomedImage) return;

    const handleMouseMove = (e) => {
      const rect = zoomedImage.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      zoomedImage.style.transformOrigin = `${x}% ${y}%`;
      zoomedImage.style.transform = 'scale(2)';
    };

    const handleMouseLeave = () => {
      zoomedImage.style.transform = 'scale(1)';
    };

    zoomedImage.addEventListener('mousemove', handleMouseMove);
    zoomedImage.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      zoomedImage.removeEventListener('mousemove', handleMouseMove);
      zoomedImage.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [zoomOpen]);

  // swiper (keep instance stable)
  const swiperInstanceRef = useRef(null);

  useEffect(() => {
    if (!swiperContainerRef.current || testimonials.length === 0) return;

    const timeout = setTimeout(() => {
      requestAnimationFrame(() => {
        const leftArrow = document.querySelector('.rbt-arrow-left');
        const rightArrow = document.querySelector('.rbt-arrow-right');

        if (!leftArrow || !rightArrow) {
          console.warn('❌ Swiper arrows not found in DOM. Swiper init skipped.');
          return;
        }

        if (swiperInstanceRef.current) {
          swiperInstanceRef.current.destroy(true, true);
        }

        swiperInstanceRef.current = new Swiper(swiperContainerRef.current, {
          slidesPerView: 1,
          spaceBetween: 30,
          loop: true,
          navigation: {
            nextEl: '.rbt-arrow-right',
            prevEl: '.rbt-arrow-left',
          },
          breakpoints: {
            768: { slidesPerView: 2 },
            1200: { slidesPerView: 3 },
          },
        });
      });
    }, 100);

    return () => clearTimeout(timeout);
  }, [testimonials]);

  if (!product) return <div>Loading...</div>;

  


  const selectedFabric = product?.fabrics?.[selectedFabricIndex] || product?.fabrics?.[0];

  // First style per styleType from API
  const firstStylesByType = (() => {
    const seen = new Set();
    const out = [];
    (selectedFabric?.styles || []).forEach((s) => {
      if (!seen.has(s.styleType)) {
        seen.add(s.styleType);
        out.push(s);
      }
    });
    return out;
  })();

  const getAccessorySelectedPayload = () => {
    return accessoryOptionGroups
      .map((group) => {
        const selectedValue = selectedAccessoryValues[group.key];
        const selectedOption = group.options.find((opt) => opt.value === selectedValue) || null;
        if (!selectedOption) return null;
        return {
          key: group.key,
          label: group.label,
          displayType: group.displayType,
          value: selectedOption.value,
          optionLabel: selectedOption.label,
          priceImpact: Number(selectedOption.priceImpact || 0) || 0,
        };
      })
      .filter(Boolean);
  };

  const getAccessoryPriceImpactSum = () => {
    return getAccessorySelectedPayload().reduce((sum, item) => sum + (Number(item.priceImpact) || 0), 0);
  };

  const hasMissingAccessoryRequiredOptions = () => {
    if (!isAccessoriesMainCategory) return false;
    return accessoryOptionGroups.some((group) => group.required && !selectedAccessoryValues[group.key]);
  };

  const currentDisplayPrice = (() => {
    const base = hoveredPrice !== null
      ? Number(hoveredPrice || 0)
      : Number(selectedFabric?.defaultTotalPrice || 0);

    if (!isAccessoriesMainCategory) return base;
    return base + getAccessoryPriceImpactSum();
  })();

  const handleColorChange = (index) => {
    setSelectedFabricIndex(index);
    if (product.fabrics[index]?.images?.[0]) {
      setMainImage(product.fabrics[index].images[0].imageURL);
    } else if (product.fabrics[index]?.displayImage) {
      setMainImage(product.fabrics[index].displayImage);
    }
  };

  const handleThumbnailClick = (imgUrl) => {
    setMainImage(imgUrl);
  };

  const handleAddToBag = async () => {
    if (!product || !selectedFabric) return;

    if (!isAccessoriesMainCategory && !selectedSizeType) {
      setSizeError(true);
      return;
    }
    setSizeError(false);

    if (hasMissingAccessoryRequiredOptions()) {
      setAccessoryError('Please select all required accessory options.');
      return;
    }
    setAccessoryError('');

    const selectedStyleIds = isAccessoriesMainCategory ? [] : firstStylesByType.map((s) => s.styleID);
    const selectedStyleNames = isAccessoriesMainCategory ? [] : firstStylesByType.map((s) => s.styleName).filter(Boolean);
    const accessorySelections = getAccessorySelectedPayload();
    const accessorySelectionsJson = accessorySelections.length ? JSON.stringify(accessorySelections) : null;
    const finalPrice = Number(selectedFabric?.defaultTotalPrice || 0) + (isAccessoriesMainCategory ? getAccessoryPriceImpactSum() : 0);

    // ✅ GUEST FLOW
    if (!isLoggedIn) {
      addGuestCartItem({
        guestCartId: (crypto?.randomUUID?.() || makeId()),
        productId: product.productID,
        fabricId: selectedFabric.fabricID,
        productName: product.productName,
        fabricName: selectedFabric.fabricName,
        fabricColour: selectedFabric.fabricColour,
        fabricImageUrl: selectedFabric.images?.[0]?.imageURL || selectedFabric.displayImage,
        styleOptionIds: selectedStyleIds,
        styleOptionNames: selectedStyleNames,
        accessorySelectionsJson,
        price: finalPrice,
        sizeType: isAccessoriesMainCategory ? null : selectedSizeType,
        sizeId: isAccessoriesMainCategory ? null : selectedSizeId,
        sizeSnapshot: getGuestSize(), // stores standard/body/shirt/mail payload if you want it later
        createdAt: Date.now(),
      });

      alert('Item added to bag!');
      return;
    }

    // ✅ LOGGED-IN FLOW
    const payload = {
      userId: numericUserId,
      productId: product.productID,
      fabricId: selectedFabric.fabricID,
      styleOptionIds: selectedStyleIds,
      accessorySelectionsJson,

      standardSizeSelectionId: !isAccessoriesMainCategory && selectedSizeType === 'standard' ? selectedSizeId : null,
      smartSizeId: !isAccessoriesMainCategory && selectedSizeType === 'smart' ? selectedSizeId : null,
      bodyMeasurementId: !isAccessoriesMainCategory && selectedSizeType === 'body' ? selectedSizeId : null,
      shirtMeasurementId: !isAccessoriesMainCategory && selectedSizeType === 'shirt' ? selectedSizeId : null,
      isMailUsSelected: !isAccessoriesMainCategory && selectedSizeType === 'mail',

      price: finalPrice,
    };

    const res = await fetch(`${API_BASE_URL}/UserManagement/AddToCart`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      alert(data.message || 'Failed to add to bag');
      return;
    }

    alert('Item added to bag!');
    window.dispatchEvent(new Event('ccc_cart_updated'));
  };

  const handleAddToWishlist = async () => {
    if (!product || !selectedFabric) {
      alert('Product or fabric not selected.');
      return;
    }

    const selectedStyleIds = isAccessoriesMainCategory ? [] : firstStylesByType.map((s) => s.styleID);
    const selectedStyleNames = isAccessoriesMainCategory ? [] : firstStylesByType.map((s) => s.styleName).filter(Boolean);
    if (!isAccessoriesMainCategory && !selectedStyleIds.length) {
      alert('No styles selected.');
      return;
    }

    if (!isAccessoriesMainCategory && !selectedSizeType) {
      setSizeError(true);
      return;
    }
    setSizeError(false);

    if (hasMissingAccessoryRequiredOptions()) {
      setAccessoryError('Please select all required accessory options.');
      return;
    }
    setAccessoryError('');

    const accessorySelections = getAccessorySelectedPayload();
    const accessorySelectionsJson = accessorySelections.length ? JSON.stringify(accessorySelections) : null;
    const finalPrice = Number(selectedFabric?.defaultTotalPrice || 0) + (isAccessoriesMainCategory ? getAccessoryPriceImpactSum() : 0);

    // ✅ GUEST FLOW: write to session wishlist, DO NOT call API
    if (!isLoggedIn) {
      const guestSize = getGuestSize();

      addGuestWishlistItem({
        guestWishlistId: makeId(),
        productId: product.productID,
        fabricId: selectedFabric.fabricID,
        productName: product.productName,
        fabricName: selectedFabric.fabricName,
        fabricColour: selectedFabric.fabricColour,
        fabricImageUrl: selectedFabric.images?.[0]?.imageURL || selectedFabric.displayImage,
        styleOptionIds: selectedStyleIds,
        styleOptionNames: selectedStyleNames,
        accessorySelectionsJson,
        price: finalPrice,
        sizeType: isAccessoriesMainCategory ? null : selectedSizeType,
        sizeId: isAccessoriesMainCategory ? null : selectedSizeId,
        sizeSnapshot: guestSize,
        createdAt: Date.now(),
      });

      alert('Item added to wishlist!');
      return;
    }

    // ✅ LOGGED-IN FLOW
    const payload = {
      userId: numericUserId,
      productId: product.productID,
      fabricId: selectedFabric.fabricID,
      styleOptionIds: selectedStyleIds,
      accessorySelectionsJson,

      standardSizeSelectionId: !isAccessoriesMainCategory && selectedSizeType === 'standard' ? selectedSizeId : null,
      smartSizeId: !isAccessoriesMainCategory && selectedSizeType === 'smart' ? selectedSizeId : null,
      bodyMeasurementId: !isAccessoriesMainCategory && selectedSizeType === 'body' ? selectedSizeId : null,
      shirtMeasurementId: !isAccessoriesMainCategory && selectedSizeType === 'shirt' ? selectedSizeId : null,
      isMailUsSelected: !isAccessoriesMainCategory && selectedSizeType === 'mail',

      price: finalPrice,
    };

    try {
      const res = await fetch(`${API_BASE_URL}/UserManagement/AddToWishlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data.message || 'Failed to add to wishlist');
        return;
      }

      alert('Item added to wishlist!');
      window.dispatchEvent(new Event('ccc_wishlist_updated'));
    } catch (err) {
      console.error('Error adding to wishlist:', err);
      alert('Something went wrong.');
    }
  };

  return (
    <>
      {showLoginPopup && (
        <div className={`size-overlay show`}>
          <button
            className="size-close-btn"
            onClick={() => setShowLoginPopup(false)}
            type="button"
          >
            ✖
          </button>

          <div
            className="size-guide-content"
            style={{
              maxWidth: '520px',
              margin: '60px auto 0',
              background: '#fff',
              borderRadius: '12px',
              padding: '22px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
            }}
          >
            <h3 style={{ marginBottom: '8px' }}>Please log in first</h3>
            <p style={{ marginBottom: '16px', color: '#555' }}>
              You only need to log in to create Smart Sizes or save measurements to your account.
              Standard Size / Body / Shirt selection works as a guest and will be saved in this browser
              session.
            </p>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="rbt-btn btn-border"
                onClick={() => setShowLoginPopup(false)}
              >
                Cancel
              </button>
              <button type="button" className="rbt-btn btn-gradient" onClick={goToLogin}>
                Go to Login
              </button>
            </div>
          </div>
        </div>
      )}

      <Header />
      <main className="rbt-main-wrapper">
        <section>
          <div className="product-container row">
            {/* ---------- IMAGE GALLERY ---------- */}
            <div className="image-gallery col-lg-7">
              <div className="thumbnail-list">
                {selectedFabric.images.map((img, idx) => {
                  const isVideo = (img.imageURL || '').endsWith('.mp4');
                  return isVideo ? (
                    <video
                      key={img.fabricImageID || `${idx}`}
                      className={`thumbnail-img ${img.imageURL === mainImage ? 'active' : ''}`}
                      src={img.imageURL}
                      onClick={() => handleThumbnailClick(img.imageURL)}
                      muted
                      playsInline
                      preload="metadata"
                      width="80"
                      height="80"
                    />
                  ) : (
                    <img
                      key={img.fabricImageID || `${idx}`}
                      src={img.imageURL}
                      className={`thumbnail-img ${img.imageURL === mainImage ? 'active' : ''}`}
                      onClick={() => handleThumbnailClick(img.imageURL)}
                      alt={`Fabric Image ${idx + 1}`}
                    />
                  );
                })}
              </div>

              <div className="main-image-container">
                <div className="badge-container">
                  {product.tags?.map((tag) => (
                    <span key={tag.tagId} className="product-badge">
                      {tag.name}
                    </span>
                  ))}
                </div>

                {mainImage.endsWith('.mp4') ? (
                  <video
                    src={mainImage}
                    className="main-image"
                    id="mainImage"
                    controls
                    autoPlay
                    muted
                    loop
                    onClick={() => setZoomOpen(true)}
                  />
                ) : (
                  <img
                    src={mainImage}
                    className="main-image"
                    id="mainImage"
                    alt="Main Product"
                    onClick={() => setZoomOpen(true)}
                  />
                )}
              </div>

              {/* Zoom popup */}
              {zoomOpen && (
                <div id="zoomPopup" className="zoom-popup">
                  <span className="zoom-close-btn" onClick={() => setZoomOpen(false)}>
                    ×
                  </span>
                  <div className="zoom-container">
                    {mainImage.endsWith('.mp4') ? (
                      <video src={mainImage} className="zoom-image" autoPlay muted loop controls />
                    ) : (
                      <img ref={zoomedImageRef} src={mainImage} className="zoom-image" alt="Zoomed Product" />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* ---------- PRODUCT DETAILS ---------- */}
            <div className="product-details col-lg-5">
              <h3 className="product-title">{product.productName}</h3>

              <div className="product-review">
                <div className="star-rating">★★★★★</div>
                <span className="review-count">(2552)</span>
              </div>

              <p className="product-price">
                $
                {Number(currentDisplayPrice || 0).toFixed(2)}
              </p>

              <p className="selected-color">
                <span id="currentColor">{hoveredFabricColor || selectedFabric.fabricColour}</span>
              </p>

              <div className="color-options">
                {fabricRows.map((row, rowIdx) => (
                  <div className="color-options-row" key={`row-${rowIdx}`}>
                    {row.map((fabric, idxInRow) => {
                      const idx = rowIdx * 10 + idxInRow; // ✅ original index
                      return (
                        <div
                          key={fabric.fabricID}
                          className={`color-swatch ${idx === selectedFabricIndex ? 'active' : ''}`}
                          style={{
                            backgroundImage: `url(${fabric.displayImage})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                          onClick={() => handleColorChange(idx)}
                          onMouseEnter={() => {
                            setHoveredFabricColor(fabric.fabricColour);
                            setHoveredPrice(fabric.defaultTotalPrice ?? 0);
                          }}
                          onMouseLeave={() => {
                            setHoveredFabricColor(null);
                            setHoveredPrice(null);
                          }}
                          title={fabric.fabricColour}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>

              {isAccessoriesMainCategory && accessoryOptionGroups.length > 0 && (
                <div className="accessory-options" style={{ marginTop: '20px' }}>
                  {accessoryOptionGroups.map((group) => (
                    <div key={group.key} style={{ marginBottom: '18px' }}>
                      <p style={{ marginBottom: '8px', fontWeight: 600 }}>
                        {group.label}
                        {group.required ? ' *' : ''}
                      </p>

                      {group.displayType === 'dropdown' && (
                        <select
                          className="form-control"
                          value={selectedAccessoryValues[group.key] || ''}
                          onChange={(e) =>
                            setSelectedAccessoryValues((prev) => ({ ...prev, [group.key]: e.target.value }))
                          }
                        >
                          <option value="">Select {group.label}</option>
                          {group.options.map((opt) => (
                            <option key={`${group.key}-${opt.value}`} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      )}

                      {(group.displayType === 'button' || group.displayType === 'buttons') && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                          {group.options.map((opt) => {
                            const active = selectedAccessoryValues[group.key] === opt.value;
                            return (
                              <button
                                key={`${group.key}-${opt.value}`}
                                type="button"
                                onClick={() =>
                                  setSelectedAccessoryValues((prev) => ({ ...prev, [group.key]: opt.value }))
                                }
                                style={{
                                  border: active ? '2px solid #111' : '1px solid #ccc',
                                  background: '#fff',
                                  padding: '8px 14px',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                }}
                              >
                                {opt.label}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {group.displayType === 'swatch' && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                          {group.options.map((opt) => {
                            const active = selectedAccessoryValues[group.key] === opt.value;
                            return (
                              <button
                                key={`${group.key}-${opt.value}`}
                                type="button"
                                title={opt.label}
                                onClick={() =>
                                  setSelectedAccessoryValues((prev) => ({ ...prev, [group.key]: opt.value }))
                                }
                                style={{
                                  width: '40px',
                                  height: '40px',
                                  border: active ? '2px solid #111' : '1px solid #ccc',
                                  borderRadius: '4px',
                                  backgroundImage: opt.swatchImageUrl ? `url(${opt.swatchImageUrl})` : 'none',
                                  backgroundSize: 'cover',
                                  backgroundPosition: 'center',
                                  cursor: 'pointer',
                                }}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}

                  {accessoryError && (
                    <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#d00' }}>{accessoryError}</div>
                  )}
                </div>
              )}

              {!isAccessoriesMainCategory && (
              <div className="size-selection">
                <div id="sizeDropdown" className="dropdown" onClick={() => setShowSizePanel(true)}>
                  <span>{selectedSizeType ? 'Change Size Selection' : 'Select Your Size'}</span>
                </div>

                {selectedSizeType && (
                  <div style={{ marginTop: '10px', fontSize: '0.9rem', color: '#333' }}>
                    Size Selected
                  </div>
                )}

                {sizeError && !selectedSizeType && (
                  <div style={{ marginTop: '10px', fontSize: '0.9rem', color: '#d00' }}>
                    Please select your size
                  </div>
                )}
              </div>
              )}

              {!isAccessoriesMainCategory && (
              <div className={`size-panel ${showSizePanel ? 'show' : ''}`} id="sizePanel">
                <div className="size-panel-header">
                  <span>Select Your Size</span>
                  <span className="close-btn" id="closePanel" onClick={() => setShowSizePanel(false)}>
                    &times;
                  </span>
                </div>

                <div className="tabs">
  {standardCfg.tabs.includes("standard") && (
    <div
      className={`tab ${activeSizeTab === "standard" ? "active" : ""}`}
      onClick={() => setActiveSizeTab("standard")}
    >
      STANDARD SIZES
    </div>
  )}

  {standardCfg.tabs.includes("smart") && (
    <div
      className={`tab ${activeSizeTab === "smart" ? "active" : ""}`}
      onClick={() => setActiveSizeTab("smart")}
    >
      SMART SIZES
    </div>
  )}

  {standardCfg.tabs.includes("body") && (
    <div
      className={`tab ${activeSizeTab === "body" ? "active" : ""}`}
      onClick={() => setActiveSizeTab("body")}
    >
      MEASURE YOUR BODY
    </div>
  )}

  {standardCfg.tabs.includes("shirt") && (
    <div
      className={`tab ${activeSizeTab === "shirt" ? "active" : ""}`}
      onClick={() => setActiveSizeTab("shirt")}
    >
      MEASURE YOUR SHIRT
    </div>
  )}

  {standardCfg.tabs.includes("mail") && (
    <div
      className={`tab ${activeSizeTab === "mail" ? "active" : ""}`}
      onClick={() => setActiveSizeTab("mail")}
    >
      MAIL US A SHIRT
    </div>
  )}
</div>


                <div className="size-content">
                  {activeSizeTab === 'standard' && (
                    <div className="size-section">
                      <div className="rbt-my-account-inner">
                        <h3 className="mb--20">{standardCfg.title}</h3>

                        <div
                          className="standard-size-card"
                          style={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '12px',
                            padding: '24px',
                            maxWidth: '640px',
                            boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
                          }}
                        >
                          {/* Header */}
                          <div
                            className="standard-size-header"
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '24px',
                            }}
                          >
                            <div style={{ display: 'flex', gap: '24px', fontSize: '0.85rem', letterSpacing: '0.08em' }}>
                              <span style={{ fontWeight: 600, borderBottom: '2px solid #000', paddingBottom: '4px' }}>
                                STANDARD SIZES
                              </span>
                            </div>
                          </div>

                          {Object.entries(standardCfg.fields).map(([key, field]) => {
  const options =
    field.dynamic === "shirtSleeveByCollar"
      ? sleeveOptionsForCurrentCollar.map((o) => ({ value: String(o.value), sub: o.label }))
      : (field.options || []);

  return (
    <div key={key} style={{ marginBottom: "24px" }}>
      <div style={{ fontSize: "0.78rem", letterSpacing: "0.12em", fontWeight: 600, marginBottom: "12px" }}>
        {field.label}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "12px" }}>
        {options.map((opt) => {
          const value = typeof opt === "string" ? opt : opt.value;
          const sub = typeof opt === "string" ? "" : (opt.sub || "");

          const active = String(standardSize[key] || "") === String(value);

          return (
            <button
              key={value}
              type="button"
              onClick={() => handleStandardSelect(key, String(value))}
              style={{
                borderRadius: "6px",
                border: active ? "2px solid #000" : "1px solid #d0d0d0",
                backgroundColor: active ? "#3d4048" : "#fff",
                color: active ? "#fff" : "#111",
                padding: "10px 8px",
                fontSize: "0.95rem",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: active ? "0 0 0 3px rgba(0,0,0,0.15)" : "none",
                cursor: "pointer",
                position: "relative",
              }}
            >
              <span>{value}</span>

              {sub ? (
                <span style={{ fontSize: "0.7rem", color: active ? "#e0e0e0" : "#888", marginTop: "4px" }}>
                  {sub}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
})}


                          <button type="button" className="rbt-btn btn-gradient" onClick={handleSaveStandardSize}>
                            Save Standard Size
                          </button>
                        </div>

                        {/* Saved Sizes (logged in only) */}
                        {isLoggedIn && standardSizeList.length > 0 && (
  <div className="rbt-my-account-table table-responsive text-center mt--30">
    <table className="table table-bordered">
      <thead>
        <tr>
          <th>Select</th>

          {Object.entries(standardCfg.fields).map(([key, field]) => (
            <th key={key}>{field.label}</th>
          ))}

          <th>Actions</th>
        </tr>
      </thead>

      <tbody>
        {standardSizeList.map((ss) => (
          <tr key={ss.standardSizeSelectionId}>
            <td>
              <input
                type="radio"
                name="sizeSelection"
                className="size-radio"
                checked={selectedSizeType === "standard" && selectedSizeId === ss.standardSizeSelectionId}
                onChange={() => {
                  setSelectedSizeType("standard");
                  setSelectedSizeId(ss.standardSizeSelectionId);
                  setSizeError(false);
                  setShowSizePanel(false);
                }}
              />
            </td>

            {Object.keys(standardCfg.fields).map((k) => (
              <td key={k}>{ss?.[k] || "-"}</td>
            ))}

            <td>
              <button className="rbt-btn btn-gradient btn-sm" onClick={() => handleEditStandardSize(ss)}>
                Edit
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)}

                      </div>
                    </div>
                  )}

                  {activeSizeTab === 'smart' && (
                    <div className="size-section">
                      {(!Array.isArray(smartSizes) || smartSizes.length === 0) ? (
                        <div id="smart-size-empty-state" style={{
                          padding: '3rem 2rem',
                          border: '1px solid #e0e0e0',
                          borderRadius: '12px',
                          backgroundColor: '#fafafa',
                          maxWidth: '900px',
                          margin: '0 auto',
                          boxShadow: '0 0 10px rgba(0,0,0,0.03)'
                        }}>
                          <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1.5rem', color: '#1d1d1f' }}>
                            Create a Size
                          </h2>

                          <div style={{
                            padding: '2rem',
                            backgroundColor: '#fff',
                            borderRadius: '10px',
                            border: '1px solid #ddd',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                              <h3 style={{
                                fontSize: '1.25rem',
                                fontWeight: '600',
                                color: '#333',
                                marginRight: '1rem',
                                marginBottom: 0
                              }}>
                                Smart Sizes
                              </h3>
                              <span style={{
                                backgroundColor: '#e0e0e0',
                                color: '#333',
                                fontSize: '0.75rem',
                                padding: '0.2rem 0.6rem',
                                borderRadius: '999px'
                              }}>
                                Most Popular
                              </span>
                            </div>

                            <p style={{
                              fontSize: '1rem',
                              color: '#555',
                              marginBottom: '1.5rem',
                              lineHeight: '1.6'
                            }}>
                              Answer a few simple questions about your body and let our algorithms calculate your ideal custom size.
                            </p>

                            <button
                              type="button"
                              onClick={() => {
                                // save current page so wizard can come back here
                                sessionStorage.setItem(
                                  SESSION_RETURN_KEY,
                                  location.pathname + location.search + location.hash
                                );

                                // go to wizard (guest or logged in)
                                navigate(isLoggedIn ? `/smartSizes/new/${numericUserId}` : `/smartSizes/new/0`);
                              }}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#000',
                                color: '#fff',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '6px',
                                textDecoration: 'none',
                                fontWeight: '600',
                                fontSize: '0.9rem',
                                border: 'none',
                                cursor: 'pointer'
                              }}
                            >
                              USE SMART SIZES
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="rbt-smart-sizes-section">
                          <div className="rbt-smart-sizes-header">
                            <h3>Saved Smart Sizes</h3>
                            <div className="rbt-smart-sizes-tabs">
                              <button className="rbt-tab active">Shirts</button>
                            </div>
                            <button
                              type="button"
                              className="rbt-new-size-btn"
                              onClick={() => {
                                sessionStorage.setItem(
                                  SESSION_RETURN_KEY,
                                  location.pathname + location.search + location.hash
                                );

                                navigate(isLoggedIn ? `/smartSizes/new/${numericUserId}` : `/smartSizes/new/0`);
                              }}
                            >
                              + Create a New Size
                            </button>
                          </div>

                          <div className="rbt-smart-sizes-list" id="rbt-tab-content">
                            {smartSizes.map((size, i) => (
                              <div className="rbt-size-item" key={size.smartSizeId || i} style={{ display: 'flex', alignItems: 'center', padding: '10px 0' }}>
                                <input
                                  type="radio"
                                  name="sizeSelection"
                                  checked={selectedSizeType === 'smart' && selectedSizeId === size.smartSizeId}
                                  onChange={() => {
                                    setSelectedSizeType('smart');
                                    setSelectedSizeId(size.smartSizeId);
                                    setShowSizePanel(false);
                                    setSelectedSmartSizeId(size.smartSizeId);
                                    setSizeError(false);
                                  }}
                                  className="size-radio"
                                />

                                <div className="rbt-size-info" style={{ flexGrow: 1 }}>
                                  <strong>Smart Sizes – {size.name || `Size ${i + 1}`}</strong>
                                </div>

                                <button
                                  className="rbt-edit-btn"
                                  onClick={() => {
                                    sessionStorage.setItem(
                                      SESSION_RETURN_KEY,
                                      location.pathname + location.search + location.hash
                                    );

                                    handleEditSmartSize(size); // this will navigate
                                  }}
                                >
                                  View / Edit
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {activeSizeTab === 'body' && (
                    <div className="size-section">
                      <p style={{ marginTop: '10px', color: '#555' }}>
                        Provide your actual body measurements to ensure a perfect tailored fit. Our expert tailors will use these measurements to craft a shirt that's designed to match your body shape precisely.
                      </p>
                      <button
                        className="rbt-btn btn-gradient mt--20"
                        onClick={() => {
                          setSelectedBodyMeasurement(null);
                          setShowBodyPopup(true);
                        }}
                      >
                        + Add Measurement
                      </button>

                      <div className="rbt-my-account-table table-responsive text-center mt--30">
                        {bodyMeasurements.length === 0 ? (
                          <div style={{
                            padding: '2rem',
                            border: '1px solid #e0e0e0',
                            borderRadius: '12px',
                            backgroundColor: '#f9f9f9',
                            textAlign: 'center'
                          }}>
                            <h4 style={{ fontWeight: '600', fontSize: '1.5rem', marginBottom: '1rem', color: '#333' }}>
                              {isLoggedIn ? 'No Body Measurements Saved' : 'Guest Mode'}
                            </h4>
                            <p style={{ color: '#777', lineHeight: '1.6' }}>
                              {isLoggedIn
                                ? 'To ensure your shirts fit perfectly, please add your body measurements.'
                                : 'You can add a Body Measurement as a guest. It will be saved in this browser session.'}
                            </p>
                          </div>
                        ) : (
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>Select</th>
                                <th>Neck</th>
                                <th>Sleeve</th>
                                <th>Chest</th>
                                <th>Fit</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {bodyMeasurements.map((measure) => (
                                <tr key={measure.bodyMeasurementId}>
                                  <td>
                                    <input
                                      type="radio"
                                      name="sizeSelection"
                                      checked={selectedSizeType === 'body' && selectedSizeId === measure.bodyMeasurementId}
                                      onChange={() => {
                                        setSelectedSizeType('body');
                                        setSelectedSizeId(measure.bodyMeasurementId);
                                        setShowSizePanel(false);
                                        setSizeError(false);
                                      }}
                                      className="size-radio"
                                    />
                                  </td>
                                  <td>{measure.neckAround || '-'}</td>
                                  <td>{measure.sleeveLength || '-'}</td>
                                  <td>{measure.chestAround || '-'}</td>
                                  <td>{measure.fit || '-'}</td>
                                  <td>
                                    <button
                                      onClick={() => {
                                        setSelectedBodyMeasurement(measure);
                                        setShowBodyPopup(true);
                                      }}
                                      className="rbt-btn btn-gradient btn-sm"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteBodyMeasurement(measure.bodyMeasurementId)}
                                      className="rbt-btn btn-danger btn-gradient btn-sm ms--10"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}

                  {activeSizeTab === 'shirt' && (
                    <div className="size-section">
                      <p style={{ marginTop: '10px', color: '#555' }}>
                        Add measurements from your favorite well-fitting shirt to help us create a perfect replica tailored to your preferences.
                      </p>

                      <button
                        className="rbt-btn btn-gradient mt--20"
                        onClick={() => {
                          setSelectedShirtMeasurement(null);
                          setShirtForm({
                            collarAround: '',
                            sleeveLength: '',
                            yokeWidth: '',
                            chestWidth: '',
                            midsectionWidth: '',
                            shirtLength: '',
                            sleeveWidth: '',
                            cuffAround: ''
                          });
                          setShowShirtPopup(true);
                        }}
                      >
                        + Add Shirt Measurement
                      </button>

                      <div className="rbt-my-account-table table-responsive text-center mt--30">
                        {shirtMeasurements.length === 0 ? (
                          <div style={{ padding: '2rem', border: '1px solid #ddd', borderRadius: '12px', backgroundColor: '#f9f9f9' }}>
                            <h4 style={{ fontWeight: '600', fontSize: '1.5rem', marginBottom: '1rem', color: '#333' }}>
                              {isLoggedIn ? 'No Shirt Measurements Saved' : 'Guest Mode'}
                            </h4>
                            <p style={{ color: '#777', lineHeight: '1.6' }}>
                              {isLoggedIn
                                ? 'To ensure the perfect replica of your favorite shirt, please add shirt measurements.'
                                : 'You can add a Shirt Measurement as a guest. It will be saved in this browser session.'}
                            </p>
                          </div>
                        ) : (
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>Select</th>
                                <th>Collar</th>
                                <th>Sleeve</th>
                                <th>Chest</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {shirtMeasurements.map((measure) => (
                                <tr key={measure.shirtMeasurementId}>
                                  <td>
                                    <input
                                      type="radio"
                                      name="sizeSelection"
                                      checked={selectedSizeType === 'shirt' && selectedSizeId === measure.shirtMeasurementId}
                                      onChange={() => {
                                        setSelectedSizeType('shirt');
                                        setSelectedSizeId(measure.shirtMeasurementId);
                                        setShowSizePanel(false);
                                        setSizeError(false);
                                      }}
                                      className="size-radio"
                                    />
                                  </td>
                                  <td>{measure.collarAround || '-'}</td>
                                  <td>{measure.sleeveLength || '-'}</td>
                                  <td>{measure.chestWidth || '-'}</td>
                                  <td>
                                    <button
                                      onClick={() => {
                                        setSelectedShirtMeasurement(measure);
                                        setShirtForm({
                                          collarAround: measure.collarAround || '',
                                          sleeveLength: measure.sleeveLength || '',
                                          yokeWidth: measure.yokeWidth || '',
                                          chestWidth: measure.chestWidth || '',
                                          midsectionWidth: measure.midsectionWidth || '',
                                          shirtLength: measure.shirtLength || '',
                                          sleeveWidth: measure.sleeveWidth || '',
                                          cuffAround: measure.cuffAround || '',
                                        });
                                        setShowShirtPopup(true);
                                      }}
                                      className="rbt-btn btn-gradient btn-sm"
                                    >
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteShirtMeasurement(measure.shirtMeasurementId)}
                                      className="rbt-btn btn-danger btn-gradient btn-sm ms--10"
                                    >
                                      Delete
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}

                  {activeSizeTab === 'mail' && (
                    <div className="size-section">
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                        <input
                          type="radio"
                          name="sizeSelection"
                          checked={selectedSizeType === 'mail'}
                          onChange={() => {
                            setSelectedSizeType('mail');
                            setSelectedSizeId('mail');
                            setShowSizePanel(false);
                            setSizeError(false);

                            if (!isLoggedIn) {
                              saveGuestSize({ sizeType: 'mail', sizeId: 'mail' });
                            }
                          }}
                          className="size-radio"
                          style={{ marginRight: '10px' }}
                        />
                        <span style={{ fontWeight: '500', color: '#333' }}>Shirt Mailed to Measure</span>
                      </div>

                      <p style={{ marginTop: '10px', color: '#555' }}>
                        Want to copy the fit of a shirt you already have, but aren’t sure how to measure it properly?
                      </p>
                      <p style={{ color: '#555' }}>
                        Mail us your shirt and our experts will measure it and create a special saved size in your account for you.
                        <br />
                        <em>*Note that our measuring causes no damage to the shirt and we will return the shirt to you right away.</em>
                      </p>

                      <h5 style={{ margin: '15px 0 10px', fontWeight: '600' }}>Send to:</h5>
                      <div style={{ border: '1px solid #ddd', borderRadius: '8px', padding: '15px', background: '#f9f9f9' }}>
                        <p style={{ margin: 0 }}><strong>Capital Custom Clothier</strong></p>
                        <p style={{ margin: 0 }}>Attn: Measuring</p>
                        <p style={{ margin: 0 }}>50 Maryland Ave</p>
                        <p style={{ margin: 0 }}>Annapolis, MD 21401</p>
                      </div>

                      <h5 style={{ margin: '15px 0 10px', fontWeight: '600' }}>When sending, be sure to include:</h5>
                      <ul style={{ paddingLeft: '20px', lineHeight: '1.6', color: '#555' }}>
                        <li>The shirt you want us to measure</li>
                        <li>Your name</li>
                        <li>Phone number</li>
                        <li>Email address</li>
                        <li>Return shipping address</li>
                        <li>Any special instructions</li>
                      </ul>
                    </div>
                  )}
                </div>

                <div className="size-panel-footer">
                  <p>Free 60-Day Returns & Remakes</p>
                  <div className="size-bag">
                    {categoryKey === "shirt" && (
                      <button className="guide-btn" onClick={openSizeGuide}>
                        SIZE GUIDE
                      </button>
                    )}
                  </div>
                </div>

                {/* size guide popup */}
                <div className={`size-overlay ${showSizeGuide ? 'show' : ''}`}>
                  <button className="size-close-btn" onClick={closeSizeGuide}>
                    ✖
                  </button>
                  <h3 className="text-center">Size Guide</h3>

                  <div className="fit-div">
                    <h6>FIT</h6>
                    <div>
                      {['Slim', 'Classic', 'Athletic'].map((fit) => (
                        <button
                          key={fit}
                          className={`size-btn ${selectedFit === fit ? 'active' : ''}`}
                          onClick={() => handleUpdateSize(fit)}
                        >
                          {fit}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="size-table-container">
                    <table className="size-table">
                      <thead>
                        <tr>
                          <th>Measurements (in)</th>
                          {sizeColumns.map((size) => (
                            <th key={size}>{size}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tableRowLabels.map((label, index) => (
                          <tr key={label}>
                            <td>{label}</td>
                            {sizeColumns.map((_, colIndex) => (
                              <td key={colIndex}>{selectedTableData?.[index]?.[colIndex] ?? '-'}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="size-guide-content">
                    <div className="contents">
                      <h5>Selecting a Dress Shirt Size</h5>
                      <p>Our dress shirt standard sizes are designed to be worn tucked into the pants.</p>
                    </div>
                    <div className="contents">
                      <h5>Collar Size</h5>
                      <p>
                        The Collar Size will correspond to your neck measurement in inches. Select your normal collar size, we will add an appropriate amount for expected shrinkage. Larger collar sizes correspond to larger body sizes.
                      </p>
                    </div>
                    <div className="contents">
                      <h5>Sleeve Length</h5>
                      <p>
                        The Sleeve Length is measured from the center back of collar, over the shoulder and down to the end of the sleeve. Select your normal sleeve length, we will add an appropriate amount for expected shrinkage. Longer sleeve lengths correspond to longer body lengths.
                      </p>
                    </div>
                    <div className="fit-content contents">
                      <h3>Fit</h3>
                      <p><b>Slim</b> (most popular) fits similar to Slim fit at other American brands. It is designed to be tailored yet comfortable, with a trim sleeve and a medium taper from chest to midsection.</p>
                      <p><b>Classic</b> is a generous, roomy fit. Compared to Slim, it has a wider shoulder that will drape past the edge of the man’s shoulder, a wider chest, and meaningfully wider midsection. The cut of this shirt will fit men with stockier builds, or drape comfortably on a slimmer man.</p>
                      <p><b>Athletic</b> is for men with athletic builds that want a trim fit. Compared to Slim there is more room in the chest and shoulders and less room around the midsection. Most of the models wearing dress shirts on our site are wearing Athletic Fit.</p>
                    </div>
                    <div className="size-conversion-content">
                      <h2>Size Conversions</h2>
                      <div className="size-table-wrapper">
                        <table className="size-table">
                          <thead>
                            <tr>
                              <th>Collar Size</th>
                              <th>US Size</th>
                              <th>US Big & Tall</th>
                              <th>US/UK Collar</th>
                              <th>US/UK Chest</th>
                              <th>US/UK Waist</th>
                              <th>EU Alpha Size</th>
                              <th>EU Size</th>
                              <th>EU Shirt/Collar</th>
                              <th>FR Tops</th>
                              <th>IT</th>
                              <th>JP</th>
                              <th>KR</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td><strong>14.5</strong></td>
                              <td>XS</td>
                              <td>-</td>
                              <td>14.5</td>
                              <td>34-36</td>
                              <td>28-30</td>
                              <td>XS/S</td>
                              <td>44-46</td>
                              <td>37</td>
                              <td>44-46</td>
                              <td>44-46</td>
                              <td>1-2</td>
                              <td>90-95</td>
                            </tr>
                            <tr>
                              <td><strong>15</strong></td>
                              <td>S</td>
                              <td>-</td>
                              <td>15</td>
                              <td>38</td>
                              <td>32</td>
                              <td>M</td>
                              <td>48</td>
                              <td>38</td>
                              <td>48</td>
                              <td>48</td>
                              <td>3</td>
                              <td>100</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="contents">
                      <p className="m-0">Need Size Help?</p>
                      <div>
                        <a href="https://capitalclothiers.net/appointment">Book an appointment</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}

              <div className="buttons">
                <button className="add-to-bag" onClick={handleAddToBag}>
                  ADD TO BAG
                </button>
                <button className="add-to-bag" onClick={handleAddToWishlist}>
                  ADD TO WISHLIST
                </button>
                {selectedFabric?.isCustomizable && (
                  <Link to={`/customize/${product.productID}?fabricId=${selectedFabric.fabricID}`}>
                    <button className="customize">CUSTOMIZE</button>
                  </Link>
                )}
              </div>

              <p className="returns-policy">Free 60-Day Returns & Remakes</p>
              <p className="returns-policy">
                <b>
                  Estimated Delivery:
                  {deliveryEstimate ? ` ${deliveryEstimate}` : ' Estimated delivery date will appear here. '}
                </b>
              </p>

              <div className="product-info">
                <details>
                  <summary>DESCRIPTION</summary>
                  <p>
                    {(selectedFabric?.fabricDescription && selectedFabric.fabricDescription.trim())
                      ? selectedFabric.fabricDescription
                      : product.description}
                  </p>
                  <b>Recommended Styling</b>: {selectedFabric.suggestedStyle}.
                </details>

                <details>
                  <summary>DESIGN</summary>
                  <div className="design-options">
                    {firstStylesByType.map((s) => {
                      const description = s.styleDescription || 'No description available.';
                      return (
                        <div
                          key={s.styleType}
                          className="design-item"
                          onMouseEnter={(e) =>
                            setTooltip({ visible: true, x: e.clientX, y: e.clientY, text: description })
                          }
                          onMouseMove={(e) => setTooltip((prev) => ({ ...prev, x: e.clientX, y: e.clientY }))}
                          onMouseLeave={() => setTooltip({ visible: false, x: 0, y: 0, text: '' })}
                        >
                          <span className="item-name">{s.styleType}:</span>
                          <span className="item-description">{s.styleName || '—'}</span>
                        </div>
                      );
                    })}
                  </div>
                </details>

                {tooltip.visible && (
                  <div
                    className="extra-description active"
                    style={{
                      top: tooltip.y + 10,
                      left: tooltip.x + 10,
                      position: 'fixed',
                      zIndex: 1000,
                    }}
                  >
                    {tooltip.text}
                  </div>
                )}

                {!isAccessoriesMainCategory && (
                  <details>
                    <summary>FABRIC & CARE</summary>
                    <div className="design-options">
                      <div className="design-item">
                        <span className="item-name">Name</span>
                        <span className="item-description">{selectedFabric.fabricName}</span>
                      </div>
                      <div className="design-item">
                        <span className="item-name">Fabric ID</span>
                        <span className="item-description">{selectedFabric.fabricCustomID}</span>
                      </div>
                      <div className="design-item">
                        <span className="item-name">Weave</span>
                        <span className="item-description">{selectedFabric.weave}</span>
                      </div>
                      <div className="design-item">
                        <span className="item-name">Composition</span>
                        <span className="item-description">{selectedFabric.composition}</span>
                      </div>
                      <div className="design-item">
                        <span className="item-name">Thread Count</span>
                        <span className="item-description">{selectedFabric.threadCount}</span>
                      </div>
                      <div className="design-item">
                        <span className="item-name">Weight</span>
                        <span className="item-description">{selectedFabric.weight}</span>
                      </div>
                      <div className="design-item">
                        <span className="item-name">Opacity Rating</span>
                        <span className="item-description">{selectedFabric.opacityRating}/5</span>
                      </div>
                      <div className="design-item">
                        <span className="item-name">Care Instructions</span>
                        <span className="item-description">{selectedFabric.careInstructions}</span>
                      </div>
                    </div>
                  </details>
                )}

                <details>
                  <summary>SHIPPING & RETURNS</summary>
                  <p>
                    <b>Free US Shipping On Orders Over $260.</b>
                    <br />
                    {deliveryEstimate
                      ? `This garment will be made to order and arrive by ${deliveryEstimate}. `
                      : 'Estimated delivery date will appear here. '}
                    See delivery times for all products and countries.
                  </p>
                  <p>
                    <b>Free 60-Day Returns & Remakes. </b>
                    If you’re not entirely happy with your garment, return it free of charge within 60 days for a full refund or a remake in an adjusted size or style.
                    Customers are allotted two complimentary remakes per custom product type to fine-tune their size.
                    See full Return & Remake Policy.
                  </p>
                </details>
              </div>
            </div>
          </div>
        </section>

        <div className="rbt-testimonial-area bg-color-extra2 rbt-section-gapTop rbt-section-gapBottom">
          <div className="container">
            <div className="row">
              <div className="col-lg-12 mb--60">
                <div className="section-title text-center">
                  <h2 className="title">Reviews</h2>
                </div>
              </div>
            </div>

            <div ref={swiperContainerRef} className="testimonial-item-3-activation swiper rbt-arrow-between gutter-swiper-30">
              <div className="swiper-wrapper">
                {testimonials.map((t) => (
                  <div className="swiper-slide" key={t.testimonialID}>
                    <div className="single-slide">
                      <div className="rbt-testimonial-box">
                        <div className="inner bg-no-shadow" style={{ backgroundColor: '#bababa21' }}>
                          <div className="clint-info-wrapper">
                            <div className="client-info">
                              <h5 className="title">{t.name || 'Customer'}</h5>
                              <span>{t.location || 'Unknown'}</span>
                            </div>
                          </div>
                          <div className="description">
                            <p className="subtitle-3">{t.message}</p>
                            <div className="rating mt--20">
                              {[...Array(t.rating || 5)].map((_, starIdx) => (
                                <a href="#" key={starIdx}>
                                  <i className="fa fa-star"></i>
                                </a>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="rbt-swiper-arrow rbt-arrow-left">
                <div className="custom-overfolow">
                  <i className="rbt-icon feather-arrow-left"></i>
                  <i className="rbt-icon-top feather-arrow-left"></i>
                </div>
              </div>

              <div className="rbt-swiper-arrow rbt-arrow-right">
                <div className="custom-overfolow">
                  <i className="rbt-icon feather-arrow-right"></i>
                  <i className="rbt-icon-top feather-arrow-right"></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="suggested-section">
          <div className="container">
            <h2 className="suggested-title text-center">ALSO SUGGESTED</h2>
            <div className="col-12">
              <div className="suggested-products row">
                {alsoSuggestedProducts.map((item, index) => (
                  <div className="col-lg-2 col-12" key={index}>
                    <div className="suggested-item">
                      <div className="suggested-tag">Worn With</div>
                      <Link to={`/product/${item.productID}`}>
                        <img src={item.firstFabricImage} alt={item.productName} />
                      </Link>
                      <p className="suggested-name">
                        {item.productName.length > 22 ? item.productName.substring(0, 22) + '...' : item.productName}
                      </p>
                      <p className="suggested-price">${item.firstFabricDefaultPrice?.toFixed(2)}</p>
                    </div>
                  </div>
                ))}
                {alsoSuggestedProducts.length === 0 && <p className="text-center">No suggested products available.</p>}
              </div>
            </div>
          </div>
        </section>

        <Footer />

        {showBodyPopup && (
          <div className="measure-popup-overlay show-measure-popup">
            <div className="measure-popup-content">
              <span className="measure-close-btn" onClick={() => setShowBodyPopup(false)}>
                &times;
              </span>

              <div className="measure-popup-grid">
                <div className="measure-popup-left">
                  <h3>MEASURE YOUR BODY</h3>
                  <form onSubmit={(e) => e.preventDefault()}>
                    {[
                      { key: 'neckAround', label: 'Neck Around' },
                      { key: 'sleeveLength', label: 'Sleeve Length' },
                      { key: 'shoulderWidth', label: 'Shoulder Width' },
                      { key: 'chestAround', label: 'Chest Around' },
                      { key: 'waistAround', label: 'Waist Around' },
                      { key: 'shirtLength', label: 'Shirt Length' },
                      { key: 'bicepAround', label: 'Bicep Around' },
                      { key: 'wristAround', label: 'Wrist Around' },
                    ].map((item) => (
                      <React.Fragment key={item.key}>
                        <label>{item.label}</label>
                        <div className="measure-input-group">
                          <input
                            type="text"
                            value={bodyForm[item.key]}
                            onChange={(e) => handleBodyInputChange(item.key, e.target.value)}
                            onFocus={() => setActiveBodyField(item.key)}
                          />
                          <span>in</span>
                        </div>
                      </React.Fragment>
                    ))}

                    <label>Fit</label>
                    <div
                      className={`measure-custom-select ${fitDropdownOpen ? 'active' : ''}`}
                      onClick={() => setFitDropdownOpen(!fitDropdownOpen)}
                    >
                      <div className="measure-selected">{bodyForm.fit}</div>
                      {fitDropdownOpen && (
                        <div className="measure-options">
                          {['Slim', 'Regular', 'Loose'].map((opt) => (
                            <div key={opt} className="measure-option" onClick={() => handleFitSelect(opt)}>
                              {opt}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <button type="button" className="measure-done-btn" onClick={handleSaveBodyMeasurement}>
                      DONE, CREATE SIZE.
                    </button>
                  </form>
                </div>

                <div className="measure-popup-right">
                  <h2>{activeBodyVideo?.title || 'Measurement Tips'}</h2>
                  <p>
                    {activeBodyVideo?.description ||
                      'Select a measurement field on the left to see a short video on how to measure it correctly.'}
                  </p>

                  {activeBodyVideoUrl ? (
                    <iframe
                      width="100%"
                      height="50%"
                      src={activeBodyVideoUrl}
                      title="Measurement Video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <div style={{ padding: '12px', border: '1px solid #eee', borderRadius: '8px' }}>
                      No video available for this measurement.
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>
        )}

        {showShirtPopup && (
          <div className="measureShirt-popup-overlay" id="measureShirtPopupOverlay">
            <div className="measureShirt-popup-content">
              <span className="measureShirt-close-btn" onClick={() => setShowShirtPopup(false)}>
                &times;
              </span>

              <div className="measureShirt-popup-grid">
                <div className="measureShirt-popup-left">
                  <h3>MEASURE YOUR Shirt</h3>
                  <form>
                    {[
                      { key: 'collarAround', label: 'Collar Around' },
                      { key: 'sleeveLength', label: 'Sleeve Length' },
                      { key: 'yokeWidth', label: 'Yoke Width' },
                      { key: 'chestWidth', label: 'Chest Width' },
                      { key: 'midsectionWidth', label: 'Midsection Width' },
                      { key: 'shirtLength', label: 'Shirt Length' },
                      { key: 'sleeveWidth', label: 'Sleeve Width' },
                      { key: 'cuffAround', label: 'Cuff Around' },
                    ].map((item) => (
                      <React.Fragment key={item.key}>
                        <label>{item.label}</label>
                        <div className="measureShirt-input-group">
                          <input
                            type="text"
                            value={shirtForm[item.key]}
                            onChange={(e) => handleShirtInputChange(item.key, e.target.value)}
                            onFocus={() => setActiveShirtField(item.key)}
                          />
                          <span>in</span>
                        </div>
                      </React.Fragment>
                    ))}

                    <button type="button" className="measureShirt-done-btn" onClick={handleSaveShirtMeasurement}>
                      DONE, CREATE SIZE.
                    </button>
                  </form>
                </div>

                <div className="measureShirt-popup-right">
                  <h2>{activeShirtVideo?.title || 'Measurement Tips'}</h2>
                  <p>
                    {activeShirtVideo?.description ||
                      'Select a measurement field on the left to see a short video on how to measure it correctly.'}
                  </p>

                  {activeShirtVideoUrl ? (
                    <iframe
                      width="100%"
                      height="50%"
                      src={activeShirtVideoUrl}
                      title={activeShirtVideo?.title || 'Measurement Video'}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <div style={{ padding: '12px', border: '1px solid #eee', borderRadius: '8px' }}>
                      No video available for this measurement.
                    </div>
                  )}

                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <div className="rbt-progress-parent">
        <svg className="rbt-back-circle svg-inner" width="100%" height="100%" viewBox="-1 -1 102 102">
          <path d="M50,1 a49,49 0 0,1 0,98 a49,49 0 0,1 0,-98" />
        </svg>
      </div>
    </>
  );
};

export default ProductDetails;
